import React, { useCallback, useMemo, useState } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { useNavigate } from "react-router-dom";
import { VsiBubbleFavs, VsiH4, VsiHr, VsiLgText, VsiMedText, VsiSmallText } from "../styled/App.styled";
import { Row, Col, UncontrolledTooltip, Spinner } from "reactstrap";

import { useVsiAppContext } from "../stores/VsiApp";
import { iopsLineColor, latencyLineColor, determineEnvBarColor, determineEnvBackground } from "../utils/chartUtils";
import { EnvCard, EnvHeader } from "../styled/Environment";
import themeStyles from "../utils/themeStyles";
import VsiModal from "../components/VsiModal";
import EnvironmentProgressBar from "../components/EnvironmentProgressBar";

const chartGroupings = [
  { value: "data_center", label: "Data Center", iconText: "fa-warehouse" },
  { value: "classification", label: "Classification", iconText: "fa-database" },
  { value: "geography", label: "Geography", iconText: "fa-location-dot" }
];

const devicesStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gridGap: "1rem",
  gridTemplateRows: "masonry"
};

const accumFields = {
  ttl_usable: 0,
  ttl_used: 0,
  ttl_iops: 0,
  ttl_latency: 0,
  weightedLatency: 0
};

const StorageEnvironmentCard = ({ keyData, chartGrouping, name }) => {
  const { theme } = useVsiAppContext();

  const { ttl_used, ttl_usable, ttl_iops, ttl_latency, weightedLatency } = keyData;
  const pct_used = (ttl_used / ttl_usable) * 100;
  const allDeviceTypes = Object.keys(keyData.all_device_types);

  return (
    <EnvCard>
      <EnvHeader backgroundColor={determineEnvBackground(pct_used)}>
        <div className={"d-flex flex-row align-items-center justify-content-between"}>
          <div>
            <i className={`fa-solid ${chartGrouping.iconText} me-2`}
               style={{ color: themeStyles[theme].textColor }} />
            <VsiLgText>
              {name}
            </VsiLgText>
          </div>
          <div>
            <VsiLgText>{(Math.round(ttl_usable / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} TiB</VsiLgText>
          </div>
        </div>
        <EnvironmentProgressBar value={pct_used} />
      </EnvHeader>
      <div className={"p-2"} style={devicesStyle}>
        {allDeviceTypes.map((typeKey, typeIndex) => {
          const deviceInfo = keyData.all_device_types[[typeKey]];

          return <StorageEnvironmentDeviceCard key={`device-${typeKey}-${typeIndex}`}
                                               group={name}
                                               deviceName={typeKey}
                                               deviceInfo={deviceInfo} />;
        })}
      </div>
      <div className={"p-2 d-flex flex-row align-items-center justify-content-around"}>
        {ttl_iops ? <span>
                                  <i className="pe-1 fa-solid fa-gauge"
                                     id={`${name.replace(/[.\s\\\/()]/g, "")}-iops-tip`}
                                     style={{ color: iopsLineColor }} />
                                  <UncontrolledTooltip target={`${name.replace(/[.\s\\\/()]/g, "")}-iops-tip`}>
                                      IOPs
                                  </UncontrolledTooltip>
                                  <VsiLgText>
                                      {Math.round(ttl_iops)}
                                  </VsiLgText>
                              </span> : null}
        {ttl_latency ? <span>
                                      <i className="pe-1 fa-solid fa-stopwatch"
                                         id={`${name.replace(/[.\s\\\/()]/g, "")}-latency-tip`}
                                         style={{ color: latencyLineColor }} />
                                <UncontrolledTooltip target={`${name.replace(/[.\s\\\/()]/g, "")}-latency-tip`}>
                                      Latency
                                  </UncontrolledTooltip>
                                  <VsiLgText>
                                      {Math.round(weightedLatency * 1000) / 1000}
                                  </VsiLgText>
                              </span> : null}
      </div>
    </EnvCard>
  );
};

const StorageEnvironmentDeviceCard = ({ deviceInfo, deviceName, group }) => {

  const { theme } = useVsiAppContext();
  let navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const devices = deviceInfo.devices;
  const pct_used = (deviceInfo.ttl_used / deviceInfo.ttl_usable) * 100;

  const bgColor = determineEnvBackground(pct_used);
  const barColor = determineEnvBarColor(pct_used);

  const onDeviceClick = useCallback(data => {
    navigate(`/reports/storage/device/device-summary?device_name=${encodeURI(data.device_name)}&wwn=${encodeURI(data.wwn)}&period_id=${data.period_id}`);
  }, [navigate]);

  return (
    <div className="p-2 d-flex flex-column"
         onClick={toggleModal}
         style={{
           border: `1px solid ${barColor}`,
           borderRadius: "5px",
           backgroundColor: bgColor,
           cursor: "pointer"
         }}>
      <div className={"mb-3"}>
        <i className="fa-solid fa-hard-drive me-2" style={{ color: themeStyles[theme].textColor }} />
        <VsiMedText textAlign={"left"}>
          {deviceName} ({devices.length})
        </VsiMedText>
      </div>
      <VsiMedText
        textAlign={"left"}>{(Math.round(deviceInfo.ttl_used / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} / {(Math.round(deviceInfo.ttl_usable / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} TiB</VsiMedText>

      <EnvironmentProgressBar value={Math.round(pct_used)} />
      <VsiModal isOpen={isModalOpen}
                header={<div>
                  <VsiH4>{devices.length} {deviceName}(s) in {group}</VsiH4>
                  <VsiSmallText>(double click a device name for more details)</VsiSmallText>
                </div>}
                toggle={toggleModal}>
        {devices.map((device, deviceIndex) => {
          const pctUsed = device.pct_used * 100;

          return (
            <div className={"mb-1"} key={`device-modal-${deviceName}-${deviceIndex}`}>
              <Row>
                <Col xs={9}>
                  <VsiLgText onDoubleClick={() => onDeviceClick(device)} style={{ cursor: "pointer" }}>
                    {device.device_name} ({deviceIndex + 1})
                  </VsiLgText>
                  <EnvironmentProgressBar value={Math.round(pctUsed)} />
                </Col>
                <Col xs={3} className={"d-flex flex-column align-items-center justify-content-center"}>
                  <div>
                    <i className="pe-1 fa-solid fa-gauge" style={{ color: iopsLineColor }} />
                    <VsiLgText>{device.ttl_iops?.toLocaleString?.() ?? 0}</VsiLgText>
                  </div>
                  <div>
                    <i className="pe-1 fa-solid fa-stopwatch" style={{ color: latencyLineColor }} />
                    <VsiLgText>{device?.ttl_latency?.toLocaleString?.() ?? 0}</VsiLgText>
                  </div>
                </Col>
              </Row>
              <VsiHr />
            </div>
          );
        })}
      </VsiModal>
    </div>
  );
};

export default function StorageEnvironment({ clientId, periodId, value: grouping }) {
  const chartGrouping = useMemo(() => chartGroupings.find(({ value }) => grouping === value) || chartGroupings[0], [grouping]);
  const { value: chartGroupValue } = chartGrouping;

  const route = useMemo(() => `/api/v6/report/enterprise_storage_environment_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordset: data }, { isLoading }] = useDataWithRoute(route);

  const createEnvironmentGrouping = useCallback(data => {
    /*
    Weighted latency is done with: Too Complicated and it is wrong - Sorry Instead of changing it I will recalculate at the end - RM.
        1. In each group, if weightedLatency hasn't been defaulted, default it to the first one it encounters.
        2. When looping, everytime meets a weightedLatency, do one weighted cals with current number
    */

    const grouping = {};
    data.forEach(datum => {
      const groupValue = datum?.[chartGroupValue] || "N/A";
      // If Data Center doesn't exist yet
      if (!grouping[groupValue]) {
        grouping[groupValue] = {
          ...datum,
          devicesCount: 1,
          all_device_types: {
            [datum["device_type_description"].trim()]: {
              ...datum,
              devices: [datum]
            }
          },
          weightedLatency: datum.ttl_latency  // default as the first latency it encounters
        };
      } else {
        // If device type doesn't exist yet
        if (!grouping[groupValue].all_device_types[datum["device_type_description"].trim()]) {
          // before touching the group, do weighted cals
          let wLat = 0;
          let sumIOPs = grouping[groupValue].ttl_iops + datum.ttl_latency;
          if (sumIOPs === 0) {
            sumIOPs = 1; // prevent zero divider
          }
          wLat = (grouping[groupValue].weightedLatency * grouping[groupValue].ttl_iops
            + datum.ttl_latency * datum.ttl_latency) / sumIOPs;
          grouping[groupValue].weightedLatency = wLat;

          // Adds up total per Data Center
          Object.keys(accumFields).forEach(key => {
            // skip weightedLatency
            if (key === "weightedLatency") {
              return;
            }
            grouping[groupValue][key] = grouping[groupValue][key] + datum[key];
          });

          // Adds first device to all_device_types
          grouping[groupValue].all_device_types[datum["device_type_description"].trim()] = {
            ...datum,
            devices: [datum]
          };
        } else {
          // before touching the group, do weighted cals
          let wLat = 0;
          let sumIOPs = grouping[groupValue].ttl_iops + datum.ttl_iops;
          if (sumIOPs === 0) {
            sumIOPs = 1; // prevent zero divider
          }
          wLat = (grouping[groupValue].weightedLatency * grouping[groupValue].ttl_iops
            + datum.ttl_latency * datum.ttl_iops) / sumIOPs;
          grouping[groupValue].weightedLatency = wLat;

          Object.keys(datum).forEach(key => {
            if (typeof datum[key] === "number" && !key.includes("_id")) {
              // Adds to total per Data Center
              grouping[groupValue][key] = grouping[groupValue][key] + datum[key];

              // Adds up for each device
              const numInPlace = grouping[groupValue].all_device_types[datum["device_type_description"].trim()][key] || 0;
              grouping[groupValue].all_device_types[datum["device_type_description"].trim()][key] = numInPlace + datum[key];
            }
          });

          grouping[groupValue].all_device_types[datum["device_type_description"].trim()].devices.push(datum);
        }
        grouping[groupValue].devicesCount = grouping[groupValue].devicesCount + 1;
      }
    });

    // ================RECALCULATING WEIGHTED LATENCY==========================
    Object.keys(grouping).forEach(k => {
      let key = k === "N/A" ? "" : k;
      let totalNum = 0.00;
      let totalDen = 0.00;
      data.forEach(d => {
        if (d[chartGroupValue] === key) {
          totalNum += (d.ttl_iops * d.ttl_latency);
          totalDen += d.ttl_iops;
        }
      });
      grouping[k].weightedLatency = totalDen && totalDen > 0 ? totalNum / totalDen : 0.00;
    });
    return grouping;
  }, [chartGroupValue]);

  const { envData } = useMemo(() => {
    return {
      envData: createEnvironmentGrouping(data)
    };
  }, [data, createEnvironmentGrouping]);

  const envKeys = useMemo(() => Object.keys(envData) || [], [envData]);

  return isLoading ? (
    <Spinner />
  ) : (
    <VsiBubbleFavs>
      {envKeys.map((key, index) => {
        const keyData = envData[key];
        return (
          <StorageEnvironmentCard key={`column-${key}-${index}`}
                                  name={key}
                                  chartGrouping={chartGrouping}
                                  keyData={keyData}
          />
        );
      })}
    </VsiBubbleFavs>
  );
}
